import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getSettlementsDashboard,
  markSettlementSettled,
  getSettlementsHistory,
  exportSettlementsHistoryCsv,
  getVenueAdminSettlementOverview,
} from "../../settlements/api";
import ReusableModal from "../../../components/common/ReusableModal";
import "./SettlementsDashboard.css";

const fmt = new Intl.NumberFormat("en-IN");
const money = (value) => `₹${fmt.format(Number(value || 0))}`;

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const formatDayDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  } catch {
    return "—";
  }
};

const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const daysAgoText = (value) => {
  if (!value) return "Never";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const getVenueInitials = (name = "") => {
  const clean = String(name || "").trim();

  if (!clean) return "OG";

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

const getAdminBasePath = (pathname = "") => {
  if (pathname.startsWith("/country-admin")) return "/country-admin";
  if (pathname.startsWith("/venue-admin")) return "/venue-admin";
  return "/super-admin";
};

const getRoleContext = () => {
  try {
    return JSON.parse(localStorage.getItem("va_ctx") || "{}");
  } catch {
    return {};
  }
};

const SettlementHistoryModal = ({
  open,
  loading,
  items,
  page,
  pages,
  total,
  filters,
  exporting,
  onClose,
  onFilterChange,
  onApply,
  onPageChange,
  onExportCsv,
}) => {
  return (
    <ReusableModal
      open={open}
      title="Settlements History"
      onClose={onClose}
      width="980px"
      closeOnBackdrop={false}
      className="setd-modal"
    >
      <div className="setd-history">
        <div className="setd-history-filters">
          <label className="setd-field">
            <span>Search</span>
            <input
              type="text"
              value={filters.q}
              onChange={(event) => onFilterChange("q", event.target.value)}
              placeholder="Search UTR, venue, cycle, notes"
              autoComplete="off"
            />
          </label>

          <label className="setd-field">
            <span>From</span>
            <input
              type="date"
              value={filters.from}
              onChange={(event) => onFilterChange("from", event.target.value)}
            />
          </label>

          <label className="setd-field">
            <span>To</span>
            <input
              type="date"
              value={filters.to}
              onChange={(event) => onFilterChange("to", event.target.value)}
            />
          </label>

          <div className="setd-history-actions">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              onClick={() => onApply(1)}
              disabled={loading}
            >
              {loading ? "Loading…" : "Apply"}
            </button>

            <button
              type="button"
              className="setd-btn setd-btn-primary"
              onClick={onExportCsv}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="setd-loading">Loading settlement history…</div>
        ) : items.length === 0 ? (
          <div className="setd-empty">No settled history found.</div>
        ) : (
          <div className="setd-history-wrap">
            <table className="setd-history-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Venue</th>
                  <th>Period</th>
                  <th>UTR</th>
                  <th className="setd-money-col">Venue Receives</th>
                  <th className="setd-money-col">Collected</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div>{formatDate(item.settledAt)}</div>
                      <small>{formatDateTime(item.settledAt)}</small>
                    </td>

                    <td>
                      <div>{item.venue?.name || "—"}</div>
                      <small>
                        {item.venue?.location?.city ||
                          item.venue?.country ||
                          "—"}
                      </small>
                    </td>

                    <td>
                      <div>{item.cycleLabel || "Settlement"}</div>
                      <small>
                        {formatDate(item.periodStart)} –{" "}
                        {formatDate(item.periodEnd)}
                      </small>
                    </td>

                    <td>
                      <strong>{item.utrNumber || "—"}</strong>
                    </td>

                    <td className="setd-money-col">
                      {money(item.venueSettlement)}
                    </td>

                    <td className="setd-money-col">
                      {money(item.totalCollected)}
                    </td>

                    <td>
                      <span className="setd-status-pill settled">
                        {item.status || "SETTLED"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="setd-history-footer">
          <div className="setd-history-total">Total: {total || 0}</div>

          <div className="setd-history-pager">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </button>

            <span>
              Page {page || 1} of {pages || 1}
            </span>

            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              disabled={page >= pages || loading}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </ReusableModal>
  );
};

const ReviewSettlementModal = ({
  open,
  review,
  loading,
  onClose,
  onSettleClick,
}) => {
  const settlement = review?.settlement || {};
  const summary = review?.summary || {};
  const bookings = review?.bookingsIncluded || [];

  const venueName = settlement?.venue?.name || "Settlement";
  const canSettle = settlement.canMarkAsSettled === true;

  return (
    <ReusableModal
      open={open}
      title={loading ? "Loading settlement…" : `${venueName} Settlement`}
      onClose={onClose}
      width="920px"
      closeOnBackdrop={false}
      className="setd-modal"
    >
      {loading ? (
        <div className="setd-loading">Loading settlement details…</div>
      ) : !review ? (
        <div className="setd-empty">Settlement details not found.</div>
      ) : (
        <div className="setd-review">
          <div className="setd-review-head">
            <div>
              <p className="setd-review-kicker">
                {settlement.cycleLabel || "Settlement"}
              </p>
              <h3 className="setd-review-title">
                {formatDate(settlement.periodStart)} –{" "}
                {formatDate(settlement.periodEnd)}
              </h3>
              <p className="setd-review-sub">
                Status: {settlement.status || "—"}
                {settlement.isOverdue ? " • Overdue" : ""}
              </p>
            </div>

            <div className="setd-review-total">
              <span>Venue Receives</span>
              <strong>{money(summary.venueReceives)}</strong>
            </div>
          </div>

          <div className="setd-breakdown-grid">
            <div className="setd-breakdown-card">
              <span>Collected From Players</span>
              <strong>{money(summary.collectedFromPlayers)}</strong>
            </div>

            <div className="setd-breakdown-card">
              <span>Onground Fee</span>
              <strong>{money(summary.ongroundFee)}</strong>
            </div>

            <div className="setd-breakdown-card">
              <span>Gateway Fee Borne By Onground</span>
              <strong>{money(summary.gatewayFeeBorneByOnground)}</strong>
            </div>

            <div className="setd-breakdown-card">
              <span>Onground Net Earning</span>
              <strong>{money(summary.ongroundNetEarning)}</strong>
            </div>
          </div>

          <div className="setd-bookings-head">
            <h4>Bookings Included</h4>
            <span>{summary.bookingsCount || bookings.length} booking(s)</span>
          </div>

          <div className="setd-bookings-wrap">
            <table className="setd-bookings-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>User</th>
                  <th>Ground</th>
                  <th>Date / Slot</th>
                  <th className="setd-money-col">Collected</th>
                  <th className="setd-money-col">Onground Fee</th>
                  <th className="setd-money-col">Venue Receives</th>
                </tr>
              </thead>

              <tbody>
                {bookings.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="setd-empty-cell">
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  bookings.map((booking) => (
                    <tr key={booking.paymentId}>
                      <td>{booking.bookingId || "—"}</td>
                      <td>{booking.userName || "—"}</td>
                      <td>
                        <div>{booking.groundName || "—"}</div>
                        <small>{booking.groundType || ""}</small>
                      </td>
                      <td>
                        <div>{formatDate(booking.bookingDate)}</div>
                        <small>
                          {booking.timeSlot?.start && booking.timeSlot?.end
                            ? `${booking.timeSlot.start} – ${booking.timeSlot.end}`
                            : "—"}
                        </small>
                      </td>
                      <td className="setd-money-col">
                        {money(booking.collectedAmount)}
                      </td>
                      <td className="setd-money-col">
                        {money(booking.ongroundFee)}
                      </td>
                      <td className="setd-money-col">
                        {money(booking.venueReceives)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="setd-modal-actions">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              onClick={onClose}
            >
              Close
            </button>

            {canSettle && (
              <button
                type="button"
                className="setd-btn setd-btn-primary"
                onClick={onSettleClick}
              >
                Mark as Settled
              </button>
            )}
          </div>
        </div>
      )}
    </ReusableModal>
  );
};

const SettleSettlementModal = ({
  open,
  review,
  utrNumber,
  notes,
  submitting,
  onChangeUtr,
  onChangeNotes,
  onClose,
  onConfirm,
}) => {
  const settlement = review?.settlement || {};
  const summary = review?.summary || {};
  const venueName = settlement?.venue?.name || "Settlement";
  const canConfirm = String(utrNumber || "").trim().length > 0 && !submitting;

  return (
    <ReusableModal
      open={open}
      title={`Settle ${venueName}`}
      onClose={onClose}
      width="520px"
      closeOnBackdrop={false}
      className="setd-modal"
    >
      <div className="setd-settle-form">
        <div className="setd-settle-amount">
          <span>Amount</span>
          <strong>{money(summary.venueReceives)}</strong>
        </div>

        <label className="setd-field">
          <span>UTR Number</span>
          <input
            type="text"
            value={utrNumber}
            onChange={(event) => onChangeUtr(event.target.value)}
            placeholder="Enter UTR number"
            autoComplete="off"
            disabled={submitting}
          />
          <small className="setd-field-hint">
            UTR number is mandatory before marking the settlement as paid.
          </small>
        </label>

        <label className="setd-field">
          <span>Notes</span>
          <textarea
            value={notes}
            onChange={(event) => onChangeNotes(event.target.value)}
            placeholder="Optional notes"
            rows={3}
            disabled={submitting}
          />
        </label>

        <div className="setd-modal-actions">
          <button
            type="button"
            className="setd-btn setd-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="setd-btn setd-btn-primary"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            {submitting ? "Confirming…" : "Confirm Settlement"}
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

const KpiCard = ({ variant, icon, label, value, sub }) => (
  <div className={`setd-kpi setd-kpi-${variant}`}>
    <div className="setd-kpi-icon">{icon}</div>
    <div className="setd-kpi-body">
      <p className="setd-kpi-label">{label}</p>
      <p className="setd-kpi-value">{value}</p>
      <p className="setd-kpi-sub">{sub}</p>
    </div>
  </div>
);

const SuperAdminSettlementsDashboard = ({
  refreshKey,
  onReviewSettlement,
  onOpenHistory,
}) => {
  const [summary, setSummary] = useState({});
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getSettlementsDashboard({ limit: 20 });

      setSummary(data?.summary || {});
      setPending(data?.pending || []);
    } catch (error) {
      console.error("getSettlementsDashboard error:", error);
      toast.error("Failed to load settlements dashboard");
      setSummary({});
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard, refreshKey]);

  const nextRun = summary?.nextSettlementRun;

  return (
    <>
      <div className="setd-kpi-grid">
        <KpiCard
          variant="danger"
          icon="₹"
          label="Total Pending"
          value={money(summary?.totalPendingSettlement)}
          sub={`${summary?.venuesAwaitingPayment || 0} venues`}
        />

        <KpiCard
          variant="warning"
          icon="!"
          label="Overdue Settlements"
          value={money(summary?.overdueAmount)}
          sub={`${summary?.overdueVenues || 0} venue`}
        />

        <KpiCard
          variant="success"
          icon="✓"
          label="Settled This Month"
          value={money(summary?.settledThisMonth)}
          sub={`${summary?.settledThisMonthCount || 0} settlements`}
        />

        <KpiCard
          variant="info"
          icon="□"
          label="Next Settlement Run"
          value={formatDayDate(nextRun)}
          sub={nextRun ? "Monday 10 AM" : "Not available"}
        />
      </div>

      <div className="setd-panel">
        <div className="setd-panel-head">
          <div className="setd-panel-title-wrap">
            <h2 className="setd-panel-title">
              Pending Settlements
              <span className="setd-count-badge">{pending.length}</span>
            </h2>
            <p className="setd-panel-subtitle">Venues awaiting payment</p>
          </div>

          <div className="setd-panel-actions">
            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              onClick={loadDashboard}
              disabled={loading}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>

            <button
              type="button"
              className="setd-btn setd-btn-secondary"
              onClick={onOpenHistory}
            >
              View All History
            </button>
          </div>
        </div>

        {loading ? (
          <div className="setd-loading">Loading settlements…</div>
        ) : pending.length === 0 ? (
          <div className="setd-empty">No pending settlements found.</div>
        ) : (
          <div className="setd-list">
            {pending.map((item, index) => {
              const venueName = item?.venue?.name || "Venue";
              const city =
                item?.venue?.location?.city ||
                item?.venue?.country ||
                "—";

              return (
                <div className="setd-row" key={item._id}>
                  <div className="setd-venue">
                    <div
                      className={`setd-avatar ${index % 2 === 1 ? "setd-avatar-green" : ""
                        }`}
                    >
                      {getVenueInitials(venueName)}
                    </div>

                    <div className="setd-venue-text">
                      <p className="setd-venue-name">{venueName}</p>
                      <p className="setd-venue-city">⌖ {city}</p>
                    </div>
                  </div>

                  <div>
                    <p className="setd-meta-label">Amount Due</p>
                    <p className="setd-amount">{money(item.amountDue)}</p>
                    <p className="setd-green-text">
                      {item.unpaidBookings || 0} unpaid bookings
                    </p>
                  </div>

                  <div>
                    <p className="setd-meta-label">Last Settled</p>
                    <p
                      className={`setd-last-main ${!item.lastSettledAt ? "setd-never" : ""
                        }`}
                    >
                      {daysAgoText(item.lastSettledAt)}
                    </p>
                    <p className="setd-last-sub">
                      {item.lastSettledAt ? formatDate(item.lastSettledAt) : "—"}
                    </p>
                  </div>

                  <div className="setd-row-action">
                    <button
                      type="button"
                      className="setd-btn setd-btn-primary"
                      onClick={() => onReviewSettlement(item._id)}
                    >
                      Review &amp; Settle ›
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="setd-info">
        <div className="setd-info-left">
          <span className="setd-info-icon">i</span>
          <div>
            <p className="setd-info-title">How settlements work?</p>
            <p className="setd-info-text">
              We calculate venue earnings from successful bookings. You can
              review and mark them as settled once the payment is made.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const VenueAdminSettlementsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
const { pathname } = useLocation();
const basePath = useMemo(() => getAdminBasePath(pathname), [pathname]);

  const loadOverview = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getVenueAdminSettlementOverview();
      setOverview(data || null);
    } catch (error) {
      console.error("getVenueAdminSettlementOverview error:", error);
      toast.error("Failed to load venue settlement overview");
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const upcoming = overview?.upcomingSettlement || {};
  const pending = overview?.pendingSettlements || [];
  const paid = overview?.paidSettlements || [];

  const pendingTotal = pending.reduce(
    (sum, item) => sum + Number(item.amountDue || item.venueSettlement || 0),
    0
  );

  const paidRecentTotal = paid.reduce(
    (sum, item) => sum + Number(item.paidAmount || item.venueSettlement || 0),
    0
  );

  return (
    <>
      <div className="setd-kpi-grid">
        <KpiCard
          variant="info"
          icon="□"
          label="Upcoming Settlement"
          value={money(upcoming?.venueReceives)}
          sub={`${upcoming?.count || 0} current bookings`}
        />

        <KpiCard
          variant="danger"
          icon="₹"
          label="Pending Settlement"
          value={money(pendingTotal)}
          sub={`${pending.length} settlement(s)`}
        />

        <KpiCard
          variant="success"
          icon="✓"
          label="Paid Settlements"
          value={money(paidRecentTotal)}
          sub={`${paid.length} recent records`}
        />

        <KpiCard
          variant="warning"
          icon="!"
          label="Next Freeze"
          value={formatDayDate(upcoming?.freezeAt)}
          sub="Monday 10 AM"
        />
      </div>

      {loading ? (
        <div className="setd-loading">Loading settlement overview…</div>
      ) : (
        <div className="setd-venue-sections">
          <div className="setd-panel">
            <div className="setd-panel-head">
              <div>
                <h2 className="setd-panel-title">Upcoming Settlement</h2>
                <p className="setd-panel-subtitle">
                  Current week earnings. This is not payable yet.
                </p>
              </div>
            </div>

            <div className="setd-mini-table">
              <div className="setd-mini-row">
                <div>
                  <p className="setd-mini-main">
                    {formatDate(upcoming?.periodStart)} –{" "}
                    {formatDate(upcoming?.periodEnd)}
                  </p>
                  <p className="setd-mini-sub">
                    {upcoming?.count || 0} booking(s) included so far
                  </p>
                </div>

                <strong>{money(upcoming?.venueReceives)}</strong>
                <span className="setd-status-pill">OPEN</span>
              </div>
            </div>
          </div>

          <div className="setd-panel">
            <div className="setd-panel-head">
              <div>
                <h2 className="setd-panel-title">
                  Pending Settlements
                  <span className="setd-count-badge">{pending.length}</span>
                </h2>
                <p className="setd-panel-subtitle">
                  Frozen settlements awaiting payment.
                </p>
              </div>
            </div>

            {pending.length === 0 ? (
              <div className="setd-empty">No pending settlements.</div>
            ) : (
              <div className="setd-mini-table">
                {pending.map((item) => (
                  <div className="setd-mini-row" key={item._id}>
                    <div>
                      <p className="setd-mini-main">
                        {item.cycleLabel || "Settlement"}
                      </p>
                      <p className="setd-mini-sub">
                        {formatDate(item.periodStart)} –{" "}
                        {formatDate(item.periodEnd)}
                      </p>
                    </div>

                    <strong>{money(item.amountDue || item.venueSettlement)}</strong>

                    <div className="setd-venue-row-actions">
                      <span className="setd-status-pill">PENDING</span>
                      <button
                        type="button"
                        className="setd-btn setd-btn-secondary setd-btn-sm"
                        onClick={() => navigate(`${basePath}/settlements/review/${item._id}`)}
                      >
                        View More
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="setd-panel">
            <div className="setd-panel-head">
              <div>
                <h2 className="setd-panel-title">Paid Settlements</h2>
                <p className="setd-panel-subtitle">
                  Latest settled payout records.
                </p>
              </div>
            </div>

            {paid.length === 0 ? (
              <div className="setd-empty">No paid settlement history yet.</div>
            ) : (
              <div className="setd-mini-table">
                {paid.map((item) => (
                  <div className="setd-mini-row" key={item._id}>
                    <div>
                      <p className="setd-mini-main">
                        {item.cycleLabel || formatDate(item.settledAt)}
                      </p>
                      <p className="setd-mini-sub">
                        UTR: {item.utrNumber || "—"}
                      </p>
                    </div>

                    <strong>{money(item.paidAmount || item.venueSettlement)}</strong>

                    <div className="setd-venue-row-actions">
                      <span className="setd-status-pill settled">SETTLED</span>
                      <button
                        type="button"
                        className="setd-btn setd-btn-secondary setd-btn-sm"
                        onClick={() => navigate(`${basePath}/settlements/review/${item._id}`)}
                      >
                        View More
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const SettlementsSection = () => {

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = useMemo(() => getAdminBasePath(pathname), [pathname]);
  const ctx = useMemo(() => getRoleContext(), []);
  const isVenueAdmin = String(ctx.role || "").toLowerCase() === "venue_admin";

  const [refreshKey, setRefreshKey] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewData, setReviewData] = useState(null);

  const [settleOpen, setSettleOpen] = useState(false);
  const [utrNumber, setUtrNumber] = useState("");
  const [settleNotes, setSettleNotes] = useState("");
  const [settling, setSettling] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLimit] = useState(10);
  const [historyFilters, setHistoryFilters] = useState({
    q: "",
    from: "",
    to: "",
  });
  const [exportingHistory, setExportingHistory] = useState(false);

  const loadHistory = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? historyPage;

      setHistoryLoading(true);

      try {
        const data = await getSettlementsHistory({
          status: "SETTLED",
          page: nextPage,
          limit: historyLimit,
          q: historyFilters.q || undefined,
          from: historyFilters.from || undefined,
          to: historyFilters.to || undefined,
        });

        setHistoryItems(data.items || []);
        setHistoryPage(data.page || nextPage || 1);
        setHistoryPages(data.pages || 1);
        setHistoryTotal(data.total || 0);
      } catch (error) {
        console.error("getSettlementsHistory error:", error);
        toast.error("Failed to load settlement history");
        setHistoryItems([]);
        setHistoryPage(1);
        setHistoryPages(1);
        setHistoryTotal(0);
      } finally {
        setHistoryLoading(false);
      }
    },
    [historyFilters, historyLimit, historyPage]
  );

  const handleOpenHistory = () => {
    navigate(`${basePath}/settlements/history`);
  };

  const handleCloseHistory = () => {
    if (historyLoading || exportingHistory) return;
    setHistoryOpen(false);
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleExportHistoryCsv = async () => {
    setExportingHistory(true);

    try {
      const result = await exportSettlementsHistoryCsv({
        status: "SETTLED",
        q: historyFilters.q || undefined,
        from: historyFilters.from || undefined,
        to: historyFilters.to || undefined,
      });

      toast.success(`CSV exported: ${result.filename}`);
    } catch (error) {
      console.error("exportSettlementsHistoryCsv error:", error);
      toast.error("CSV export failed");
    } finally {
      setExportingHistory(false);
    }
  };

  const handleReviewSettlement = (settlementId) => {
    if (!settlementId) return;
    navigate(`${basePath}/settlements/review/${settlementId}`);
  };

  const handleCloseReview = () => {
    if (settling) return;

    setReviewOpen(false);
    setSettleOpen(false);
    setReviewData(null);
    setUtrNumber("");
    setSettleNotes("");
  };

  const handleOpenSettleModal = () => {
    setUtrNumber("");
    setSettleNotes("");
    setSettleOpen(true);
  };

  const handleConfirmSettlement = async () => {
    const settlementId = reviewData?.settlement?._id;
    const cleanUtr = String(utrNumber || "").trim();

    if (!settlementId) {
      toast.error("Settlement not found");
      return;
    }

    if (!cleanUtr) {
      toast.warn("UTR number is required");
      return;
    }

    setSettling(true);

    try {
      await markSettlementSettled(settlementId, {
        utrNumber: cleanUtr,
        notes: settleNotes,
      });

      toast.success("Settlement marked as settled");

      setSettleOpen(false);
      setReviewOpen(false);
      setReviewData(null);
      setUtrNumber("");
      setSettleNotes("");
      setRefreshKey((value) => value + 1);
    } catch (error) {
      console.error("markSettlementSettled error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to mark settlement as settled"
      );
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className="setd">
      <div className="setd-page-head">
        <h1 className="setd-title sa-topbar-managed-page-title">Settlements</h1>
        <p className="setd-subtitle">View and manage venue settlements</p>
      </div>

      {isVenueAdmin ? (
        <VenueAdminSettlementsDashboard />
      ) : (
        <SuperAdminSettlementsDashboard
          refreshKey={refreshKey}
          onReviewSettlement={handleReviewSettlement}
          onOpenHistory={handleOpenHistory}
        />
      )}

      {!isVenueAdmin && (
        <>
          <ReviewSettlementModal
            open={reviewOpen}
            review={reviewData}
            loading={reviewLoading}
            onClose={handleCloseReview}
            onSettleClick={handleOpenSettleModal}
          />

          <SettleSettlementModal
            open={settleOpen}
            review={reviewData}
            utrNumber={utrNumber}
            notes={settleNotes}
            submitting={settling}
            onChangeUtr={setUtrNumber}
            onChangeNotes={setSettleNotes}
            onClose={() => {
              if (!settling) setSettleOpen(false);
            }}
            onConfirm={handleConfirmSettlement}
          />
          <SettlementHistoryModal
            open={historyOpen}
            loading={historyLoading}
            items={historyItems}
            page={historyPage}
            pages={historyPages}
            total={historyTotal}
            filters={historyFilters}
            exporting={exportingHistory}
            onClose={handleCloseHistory}
            onFilterChange={handleHistoryFilterChange}
            onApply={(nextPage) => loadHistory({ page: nextPage })}
            onPageChange={(nextPage) => loadHistory({ page: nextPage })}
            onExportCsv={handleExportHistoryCsv}
          />
        </>
      )}
    </div>
  );
};

export default SettlementsSection;